import { Button, Spinner } from '@consigli/facade';
import { useExportToExcelMutation, usePackageId, useProject, useProjectId } from '@consigli/hooks';
import { Finding } from '@consigli/types';
import { MIME_TYPES, saveBlobBase64 } from '@consigli/utils';
import { startOfDay } from 'date-fns';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { createRfpMilestonesExport } from '@/util/excel-export';

import { AddMilestoneDialog } from './rfp-add-milestone-dialog';
import { TimelineChart } from './rfp-timeline-chart';
import { TimelineLegend } from './rfp-timeline-legend';
import { type MilestoneRow } from './rfp-timeline-types';
import {
  computeDomain,
  computeMonthTickData,
  computeStatus,
  countByStatus,
  parseFlexibleDate,
  parseMilestonePayload,
  positionPercent,
} from './rfp-timeline-utils';

interface RfpTimelineProps {
  findings: Finding[];
}

export const RfpTimeline: FC<RfpTimelineProps> = ({ findings }) => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const packageId = usePackageId();
  const { project } = useProject(projectId);
  const today = startOfDay(new Date());
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [exportToExcel, { isLoading: isExporting }] = useExportToExcelMutation();

  const rows = useMemo<MilestoneRow[]>(() => {
    return findings
      .map((finding) => {
        const payload = parseMilestonePayload(finding);
        if (!payload) return null;
        const parsed = parseFlexibleDate(payload.dateStr);
        if (!parsed) return null;
        const date = startOfDay(parsed);
        return {
          id: finding.id,
          milestone: payload.milestone,
          date,
          status: computeStatus(date, today),
        };
      })
      .filter((row): row is MilestoneRow => row !== null)
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [findings, today]);

  const counts = useMemo(() => countByStatus(rows), [rows]);
  const domain = useMemo(() => computeDomain(rows, today), [rows, today]);
  const { monthTicks, weekTicks, weekSegments, spansMultipleYears, timelineMinWidth } = useMemo(
    () => computeMonthTickData(domain),
    [domain],
  );

  const handleExport = useCallback(async () => {
    if (!project) return;
    try {
      const excelExport = createRfpMilestonesExport(rows, t, project.name);
      const { base64Data } = await exportToExcel({
        projectId,
        packageId,
        excelExport,
      }).unwrap();
      saveBlobBase64(`${project.name}_milestones.xlsx`, base64Data, MIME_TYPES.xlsx);
      toast.success(t('dashboard.rfp-export.success'));
    } catch {
      toast.error(t('dashboard.rfp-export.error'));
    }
  }, [project, rows, t, exportToExcel, projectId, packageId]);

  return (
    <section className="my-6" aria-labelledby="rfp-timeline-heading">
      <div className="flex items-center justify-between mb-4">
        <h3 id="rfp-timeline-heading" className="text-xl font-semibold text-day-neutral-subtle">
          {t('dashboard.rfp-timeline.heading')}
        </h3>
        <div className="flex gap-2">
          {rows.length > 0 && (
            <Button
              secondary
              rounded
              onClick={handleExport}
              disabled={isExporting}
              icon={isExporting ? () => <Spinner size="xsmall" /> : undefined}
              type="button"
            >
              {t('dashboard.rfp-export.export-milestones')}
            </Button>
          )}
          <Button primary rounded onClick={() => setIsDialogOpen(true)} type="button">
            {t('dashboard.rfp-timeline.add-milestone.button')}
          </Button>
        </div>
      </div>
      {rows.length > 0 && (
        <div className="bg-day-light-3 rounded-md border border-day-light-2 overflow-hidden">
          <TimelineLegend counts={counts} today={today} />
          <TimelineChart
            rows={rows}
            domain={domain}
            monthTicks={monthTicks}
            weekTicks={weekTicks}
            weekSegments={weekSegments}
            spansMultipleYears={spansMultipleYears}
            timelineMinWidth={timelineMinWidth}
            todayPct={positionPercent(today, domain.start, domain.end)}
          />
        </div>
      )}
      <AddMilestoneDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        projectId={projectId}
        packageId={packageId}
      />
    </section>
  );
};
